/**
 * En-tête d'écran uniforme — titre (H1 unique), description, actions primaires.
 * Donne à chaque écran le même repère visuel et le même point d'action.
 */
import { createContext, useContext, type JSX, type ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'

/**
 * Vrai quand l'écran est monté DANS un autre objet qui porte déjà son titre.
 *
 * Un écran de route rend son `<h1>` ; monté comme vue d'un objet, il en produirait un
 * SECOND — deux titres de niveau 1 sur une même page, ce qui casse la structure pour un
 * lecteur d'écran et brouille la hiérarchie visuelle.
 *
 * Le contexte évite d'ajouter une prop à chacun des six écrans concernés, et surtout de
 * l'oublier sur le septième.
 */
const ContexteImbrique = createContext(false)

export function EcranImbrique({ children }: { children: ReactNode }): JSX.Element {
  return <ContexteImbrique.Provider value={true}>{children}</ContexteImbrique.Provider>
}

export function PageHeader({ title, description, actions, icon: Icon }: {
  title: string
  description?: string
  actions?: ReactNode
  icon?: LucideIcon
}): JSX.Element | null {
  // Écran monté comme VUE d'un objet : le titre est déjà porté au-dessus, mais les ACTIONS
  // ne le sont pas.
  //
  // Une première version renvoyait `null` sans distinction — et emportait avec le titre le
  // bouton « Nouvelle story », seule façon de créer un item depuis le board. Une action ne
  // fait jamais double emploi avec un titre : ce sont deux choses différentes qui se
  // trouvaient au même endroit.
  if (useContext(ContexteImbrique)) {
    if (!actions) return null
    return (
      <div className="mb-4 flex flex-wrap items-center justify-end gap-2">{actions}</div>
    )
  }

  return (
    <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="flex items-start gap-3">
        {/* Canevas v0.5.0 (bloc_titre) : le titre de page est seul, sans pastille d'icône ; l'icône
            reste portée par l'entrée de navigation. Conservée pour les écrans < md (repère visuel). */}
        {Icon ? (
          <span className="mt-0.5 flex size-10 shrink-0 items-center justify-center rounded-lg bg-brand-subtle text-brand-text md:hidden">
            <Icon className="size-5" aria-hidden="true" />
          </span>
        ) : null}
        <div className="min-w-0">
          <h1 className="font-display text-[1.375rem] leading-7 font-semibold">{title}</h1>
          {/* `max-w-prose` (65 caractères) plutôt que `max-w-2xl` (~96 à cette taille) :
              au-delà de ~75 caractères l'œil perd la ligne au retour, et la mesure relevait
              jusqu'à 151 caractères sur les écrans d'administration
              (règle `line-length-control`, UX-D0023). */}
          {description ? (
            <p className="mt-1 max-w-prose text-sm text-muted-foreground">{description}</p>
          ) : null}
        </div>
      </div>
      {actions ? <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  )
}
